/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { DatePicker, notification, Button, Card, Col, Form, Row } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import PageContentHeader from "../../../components/common/PageContentHeader";
import {
  getRetailReports,
  getBookingReports,
  getLiveBookingReports,
  getOverAllReports,
  getExchangeReports,
  getHomeVisitTestDriveReports,
  getLeadExchangeStageService,
  getExchangeStageReport,
  getAllOrderReports,
  getDeliveryReports,
  getVinDumpReports,
  getUnmatchedStockAnalysisReports,
  getMatchedStockAnalysisReports,
  getPhysicalStockAnalysisReports,
  getCancelledBookingReports,
  getMatchingDataReport,
  getVinAllocationPossibilityReport,
} from "../../../services/master-services";
import { API_RESPONSE_CODES } from "../../../utills/constant";
import Loader from "../../../components/common/Loader";
import dayjs from "dayjs";
import InputSelect from "../../../components/common/InputSelect";

const { RangePicker } = DatePicker;

const ReportsComponent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [reportType, setReportType] = useState("");
  const [reportData, setReportData] = useState([]);
  const [tempDates, setTempDates] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [exchangeStages, setExchangeStages] = useState([]);
  const [selectedStageId, setSelectedStageId] = useState(null);

  const [form] = Form.useForm();

  const handleReportName = (value) => {
    if (!value) {
      setReportData("");
      setReportType("");
      setSelectedStageId(null);
      return;
    }

    setReportData(value);
    // Extract stage ID if it's an exchange stage report
    if (value && typeof value === 'string' && value.startsWith('exchange_stage_')) {
      const stageId = value.replace('exchange_stage_', '');
      setSelectedStageId(stageId);
    } else {
      setSelectedStageId(null);
    }
  };

  useEffect(() => {
    const fetchExchangeStages = async () => {
      try {
        const response = await getLeadExchangeStageService();
        if (response.data.status === API_RESPONSE_CODES.OK) {
          setExchangeStages(response.data.data || []);
        }
      } catch (error) {
        console.error("Error fetching exchange stages:", error);
      }
    };
    fetchExchangeStages();
  }, []);

  useEffect(() => {
    if (reportData === "home_visit_test_drive_reports") {
      setReportType("Home Visit/Test Drive Report");
    } else if (reportData === "retail_reports") {
      setReportType("Retail Report");
    } else if (reportData === "exchange_reports") {
      setReportType("Exchange Report");
    } else if (reportData === "booking_reports") {
      setReportType("Booking Report");
    } else if (reportData === "live_booking_reports") {
      setReportType("Live Booking Report");
    } else if (reportData === "over_all_reports") {
      setReportType("Over All Report");
    } else if (reportData && typeof reportData === 'string' && reportData.startsWith("exchange_stage_")) {
      const selectedStage = exchangeStages.find(stage => `exchange_stage_${stage.id}` === reportData);
      setReportType(selectedStage ? `Exchange Stage - ${selectedStage.stage_name} Report` : "Exchange Stage Report");
    } else if (reportData === "all_order_report") {
      setReportType("All Order Report");
    } else if (reportData === "delivery_report") {
      setReportType("Delivery Report");
    } else if (reportData === "vin_dump_report") {
      setReportType("VIN Dump Report");
    } else if (reportData === "unmatched_stock_analysis_report") {
      setReportType("Unmatched Stock Analysis Report");
    } else if (reportData === "matched_stock_analysis_report") {
      setReportType("Matched Stock Analysis Report");
    } else if (reportData === "physical_stock_analysis_report") {
      setReportType("Physical Stock Analysis Report");
    } else if (reportData === "cancelled_booking_report") {
      setReportType("Cancelled Booking Report");
    } else if (reportData === "matching_data_report") {
      setReportType("Matching Data Report");
    } else if (reportData === "vin_allocation_possibility_report") {
      setReportType("VIN Allocation Possibility Report");
    } else {
      setReportType("");
    }
    // Keep the same default behavior for ALL reports:
    // current month start -> current month end.
    form.setFieldsValue({ date: [currentMonthStart, currentMonthEnd] });
    setTempDates(null);
  }, [reportData, exchangeStages]);



  const currentMonthStart = dayjs().startOf("month");
  const currentMonthEnd = dayjs();

  const handleSubmit = async (values) => {
    setIsLoading(true);
    const [startDate, endDate] = values?.date || [];
    try {
      let response;
      if (reportData === "home_visit_test_drive_reports") {
        response = await getHomeVisitTestDriveReports(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
      } else if (reportData === "retail_reports") {
        response = await getRetailReports(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
      } else if (reportData === "exchange_reports") {
        response = await getExchangeReports(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
      } else if (reportData === "booking_reports") {
        response = await getBookingReports(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
      } else if (reportData === "live_booking_reports") {
        response = await getLiveBookingReports(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
      } else if (reportData === "over_all_reports") {
        response = await getOverAllReports(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
      } else if (reportData && typeof reportData === 'string' && reportData.startsWith("exchange_stage_")) {
        response = await getExchangeStageReport(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD"),
          selectedStageId
        );
      } else if (reportData === "all_order_report") {
        response = await getAllOrderReports(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
      } else if (reportData === "delivery_report") {
        response = await getDeliveryReports(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
      } else if (reportData === "vin_dump_report") {
        response = await getVinDumpReports(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
      } else if (reportData === "unmatched_stock_analysis_report") {
        response = await getUnmatchedStockAnalysisReports(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
      } else if (reportData === "matched_stock_analysis_report") {
        response = await getMatchedStockAnalysisReports(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
      } else if (reportData === "physical_stock_analysis_report") {
        response = await getPhysicalStockAnalysisReports(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
      } else if (reportData === "cancelled_booking_report") {
        response = await getCancelledBookingReports(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
      } else if (reportData === "matching_data_report") {
        response = await getMatchingDataReport();
      } else if (reportData === "vin_allocation_possibility_report") {
        response = await getVinAllocationPossibilityReport(
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
      }
      if (response.data.status === API_RESPONSE_CODES.OK) {
        const filePath = response.data.data?.path;
        const link = document.createElement("a");
        link.href = filePath;
        // link.target = '_blank';
        link.download = filePath?.split("/").pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        form.setFieldsValue({
          date: [currentMonthStart, currentMonthEnd],
          report: null,
        });
        setReportData("");
        setReportType("");
        setSelectedStageId(null);
        setTempDates(null);
        setIsLoading(false);
      }
    } catch (error) {
      notification.error({
        description: error.response?.data?.message
          ? error.response?.data?.message
          : error?.message,
      });
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const reportName = [
    {
      label: "OverAll Report",
      id: "over_all_reports",
    },
    {
      label: "Booking Report",
      id: "booking_reports",
    },
    {
      label: "Live Booking Report",
      id: "live_booking_reports",
    },
    {
      label: "Exchange Report",
      id: "exchange_reports",
    },
    {
      label: "Retail Report",
      id: "retail_reports",
    },
    {
      label: "Home Visit/Test Drive Report",
      id: "home_visit_test_drive_reports",
    },
    {
      label: "All Order Report",
      id: "all_order_report",
    },
    {
      label: "Delivery Report",
      id: "delivery_report",
    },
    {
      label: "VIN Dump Report",
      id: "vin_dump_report",
    },
    {
      label: "Unmatched Stock Analysis Report",
      id: "unmatched_stock_analysis_report",
    },
    {
      label: "Matched Stock Analysis Report",
      id: "matched_stock_analysis_report",
    },
    {
      label: "Physical Stock Analysis Report",
      id: "physical_stock_analysis_report",
    },
    {
      label: "Cancelled Booking Report",
      id: "cancelled_booking_report",
    },
    {
      label: "Matching Data Report",
      id: "matching_data_report",
    },
    {
      label: "VIN Allocation Possibility Report",
      id: "vin_allocation_possibility_report",
    },
    ...exchangeStages.map(stage => ({
      label: stage.name,
      id: `exchange_stage_${stage.id}`,
      stageId: stage.id
    }))
  ];

  return (
    <>
      <Loader loading={isLoading} />
      <div>
        <PageContentHeader title={`Reports ${reportType ? ' - ' + reportType : ''}`} />
        <Card className="filter-card mb-3">
          <Form
            layout="vertical"
            name="normal_search"
            className="login-form"
            initialValues={{
              remember: true,
              date: [currentMonthStart, currentMonthEnd],
            }}
            form={form}
            onFinish={handleSubmit}
          >
            <div className="login-input-field">
              <Row gutter={[24, 16]}>
                <Col xl={5} lg={12} md={12} sm={24} xs={24}>
                  <InputSelect
                    name="report"
                    rules={[
                      { required: true, message: "Please select report name" },
                    ]}
                    label="Report Name"
                    placeholder="Report Name"
                    options={reportName}
                    optionName={"label"}
                    onSelect={handleReportName}
                    onClear={() => {
                      setReportData("");
                      setReportType("");
                      setSelectedStageId(null);
                    }}
                    allowClear
                  />
                </Col>
                {reportData !== "matching_data_report" && (
                  <Col xl={8} lg={12} md={12} sm={24} xs={24}>
                    <Form.Item
                      name="date"
                      label="Start Date/End Date"
                      className="report-date-range"
                      rules={[
                        { required: true, message: "Please select the report date range" },
                      ]}
                    >
                      <RangePicker
                        format={"DD/MM/YYYY"}
                        onOpenChange={(open) => {
                          if (open) {
                            setTempDates(null);
                            setIsSelecting(true);
                            form.setFieldsValue({ date: undefined });
                          } else {
                            setIsSelecting(false);
                          }
                        }}
                        onChange={() => {
                          setTempDates(null);
                          setIsSelecting(false);
                        }}
                        onCalendarChange={(dates) => {
                          if (isSelecting && dates) {
                            if ((dates[0] && !dates[1]) || (!dates[0] && dates[1])) {
                              setTempDates(dates);
                            } else {
                              setTempDates(dates);
                            }
                          } else {
                            setTempDates(dates);
                          }
                        }}
                        disabledDate={(current) => {
                          if (!current) return false;

                          const today = dayjs().endOf("day");

                          if (current > today) {
                            return true;
                          }

                          if (isSelecting && tempDates) {
                            let selectedDate = null;

                            if (tempDates[0] && !tempDates[1]) {
                              selectedDate = tempDates[0];
                            } else if (!tempDates[0] && tempDates[1]) {
                              selectedDate = tempDates[1];
                            }

                            if (selectedDate) {
                              const maxDate = dayjs(selectedDate).add(6, "months").endOf("day");
                              const minDate = dayjs(selectedDate).subtract(6, "months").startOf("day");

                              if (current < minDate || current > maxDate) {
                                return true;
                              }
                            }
                          }

                          return false;
                        }}
                        className="custom-range-picker"
                      />
                    </Form.Item>
                  </Col>
                )}
                {reportData === "matching_data_report" && (
                  <Col xl={8} lg={12} md={12} sm={24} xs={24} />
                )}
                <Col xl={14} lg={12} md={12} sm={24} xs={24}>
                  <Button
                    htmlType="submit"
                    type="primary"
                    className="secondary-button reports-download-button"
                    icon={<DownloadOutlined />}
                  >
                    Download
                  </Button>
                </Col>
              </Row>
            </div>
          </Form>
        </Card>
      </div>
    </>
  );
};

export default ReportsComponent;
